import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FilmStrip, Play, ArrowsClockwise, CircleNotch, Timer, CaretDown } from '@phosphor-icons/react';
import { API_URL } from './helpers';

const POLL_INTERVAL = 8000;

export const WeeklyVideo = () => {
  const [videoInfo, setVideoInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState('');
  const [showPlayer, setShowPlayer] = useState(false);
  const [videoKey, setVideoKey] = useState(0);
  const [showScheduleMenu, setShowScheduleMenu] = useState(false);
  const [savingSchedule, setSavingSchedule] = useState(false);
  const pollRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    fetchVideoInfo();
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowScheduleMenu(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const fetchVideoInfo = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/weekly-video`, { withCredentials: true });
      setVideoInfo(res.data);
    } catch { /* no video yet */ }
    finally { setLoading(false); }
  };

  const startGeneration = async () => {
    try {
      setGenerating(true);
      setShowPlayer(false);
      setProgress('Starting video generation...');
      const res = await axios.post(`${API_URL}/api/weekly-video/generate`, {}, { withCredentials: true });
      const newJobId = res.data.job_id;

      pollRef.current = setInterval(async () => {
        try {
          const status = await axios.get(`${API_URL}/api/weekly-video/status/${newJobId}`, { withCredentials: true });
          const d = status.data;
          setProgress(d.progress || d.status);

          if (d.status === 'completed') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setGenerating(false);
            setVideoKey(prev => prev + 1);
            await fetchVideoInfo();
          } else if (d.status === 'failed') {
            clearInterval(pollRef.current);
            pollRef.current = null;
            setGenerating(false);
            setProgress(`Failed: ${d.error || 'Unknown error'}`);
          }
        } catch { /* keep polling */ }
      }, POLL_INTERVAL);
    } catch {
      setGenerating(false);
      setProgress('Failed to start generation');
    }
  };

  const setSchedule = async (frequency) => {
    setSavingSchedule(true);
    try {
      await axios.post(`${API_URL}/api/weekly-video/schedule`, { frequency }, { withCredentials: true });
      setVideoInfo(prev => ({ ...prev, schedule: frequency }));
    } catch (err) {
      console.error('Schedule error:', err);
    } finally {
      setSavingSchedule(false);
      setShowScheduleMenu(false);
    }
  };

  const scheduleLabels = videoInfo?.schedule_options || {
    daily: 'Every day', every_3_days: 'Every 3 days', weekly: 'Every week', off: 'Off'
  };
  const currentSchedule = videoInfo?.schedule || 'off';

  if (loading) {
    return (
      <div className="bg-white border border-[#f2f4f6] rounded-2xl p-6 animate-pulse">
        <div className="h-6 w-48 bg-[#f2f4f6] rounded-lg mb-4" />
        <div className="h-48 bg-[#f2f4f6] rounded-xl" />
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#f2f4f6] rounded-2xl overflow-hidden" data-testid="weekly-video-section">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0f172a] to-[#1e293b] p-4 sm:p-6 lg:p-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#7c3aed]/20 flex items-center justify-center flex-shrink-0">
              <FilmStrip size={22} className="text-[#a78bfa]" weight="fill" />
            </div>
            <div>
              <h2 className="text-white font-bold text-base sm:text-lg">Video Summary</h2>
              <p className="text-[#94a3b8] text-xs sm:text-sm">AI-generated recap with narration</p>
            </div>
          </div>

          <div className="flex items-center gap-2 self-end sm:self-auto">
            {/* Schedule dropdown */}
            <div className="relative" ref={menuRef}>
              <button
                onClick={() => setShowScheduleMenu(!showScheduleMenu)}
                className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-white/10 text-[#94a3b8] hover:bg-white/15 hover:text-white transition-all"
                data-testid="schedule-dropdown-btn"
              >
                <Timer size={14} weight="bold" />
                <span className="hidden sm:inline">{currentSchedule === 'off' ? 'Auto: Off' : scheduleLabels[currentSchedule] || currentSchedule}</span>
                <span className="sm:hidden">{currentSchedule === 'off' ? 'Off' : scheduleLabels[currentSchedule]?.split(' ').pop() || 'On'}</span>
                <CaretDown size={12} weight="bold" />
              </button>

              {showScheduleMenu && (
                <div className="absolute right-0 top-full mt-1 bg-[#1e293b] border border-white/10 rounded-xl shadow-2xl z-50 min-w-[160px] sm:min-w-[180px] overflow-hidden" data-testid="schedule-menu">
                  <p className="px-4 pt-3 pb-1 text-[10px] text-[#94a3b8] uppercase tracking-wider font-bold">Auto-Generate</p>
                  {Object.entries(scheduleLabels).map(([key, label]) => (
                    <button
                      key={key}
                      onClick={() => setSchedule(key)}
                      disabled={savingSchedule}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors flex items-center justify-between ${
                        currentSchedule === key
                          ? 'bg-[#7c3aed]/20 text-[#a78bfa] font-semibold'
                          : 'text-[#e2e8f0] hover:bg-white/5'
                      }`}
                      data-testid={`schedule-option-${key}`}
                    >
                      <span>{label}</span>
                      {currentSchedule === key && <span className="text-[#a78bfa] text-xs">Active</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Generate button */}
            <button
              onClick={startGeneration}
              disabled={generating}
              className={`flex items-center gap-1.5 text-xs font-semibold px-3 sm:px-4 py-2 rounded-lg transition-all ${
                generating
                  ? 'bg-white/10 text-[#94a3b8] cursor-not-allowed'
                  : 'bg-[#7c3aed] text-white hover:bg-[#6d28d9]'
              }`}
              data-testid="generate-video-btn"
            >
              {generating ? (
                <>
                  <CircleNotch size={14} weight="bold" className="animate-spin" />
                  <span className="hidden sm:inline">Generating...</span>
                  <span className="sm:hidden">...</span>
                </>
              ) : (
                <>
                  <ArrowsClockwise size={14} weight="bold" />
                  <span className="hidden sm:inline">{videoInfo?.has_video ? 'Regenerate' : 'Generate Now'}</span>
                  <span className="sm:hidden">{videoInfo?.has_video ? 'Redo' : 'Generate'}</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Generation progress */}
        {generating && (
          <div className="mt-5" data-testid="video-progress">
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-4 border border-white/10">
              <div className="flex items-center gap-3 mb-3">
                <CircleNotch size={18} className="text-[#a78bfa] animate-spin" weight="bold" />
                <span className="text-white text-sm font-medium">{progress}</span>
              </div>
              <div className="w-full bg-white/10 rounded-full h-1.5">
                <div className="bg-[#7c3aed] h-1.5 rounded-full animate-pulse" style={{ width: '60%' }} />
              </div>
              <p className="text-[#94a3b8] text-xs mt-2">This typically takes 3-5 minutes. You can leave this page and come back.</p>
            </div>
          </div>
        )}
      </div>

      {/* Video Player / Empty State */}
      <div className="p-3 sm:p-4 lg:p-6">
        {videoInfo?.has_video ? (
          <div>
            {showPlayer ? (
              <div className="rounded-xl overflow-hidden bg-black" data-testid="video-player">
                <video
                  key={videoKey}
                  controls
                  autoPlay
                  className="w-full rounded-xl"
                  style={{ maxHeight: '400px' }}
                >
                  <source src={`${API_URL}/api/weekly-video/stream?v=${videoKey}`} type="video/mp4" />
                  Your browser does not support video playback.
                </video>
              </div>
            ) : (
              <div
                className="w-full group relative rounded-xl overflow-hidden cursor-pointer"
                onClick={() => setShowPlayer(true)}
                data-testid="video-play-btn"
              >
                {/* Show actual video first frame as thumbnail */}
                <video
                  className="w-full rounded-xl"
                  style={{ maxHeight: '400px' }}
                  preload="metadata"
                  muted
                  playsInline
                  src={`${API_URL}/api/weekly-video/stream?v=${videoKey}#t=0.5`}
                />
                {/* Play button overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                  <div className="w-16 h-16 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform shadow-xl">
                    <Play size={28} className="text-[#0f172a] ml-1" weight="fill" />
                  </div>
                </div>
                {/* Duration badge */}
                <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur-sm text-white text-xs font-medium px-2 py-1 rounded-md">
                  {videoInfo.duration || 12}s
                </div>
              </div>
            )}

            {/* Summary frames */}
            {videoInfo.text_frames && (
              <div className="mt-3 sm:mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3" data-testid="video-summary-frames">
                {videoInfo.text_frames.map((frame, i) => (
                  <div key={i} className="bg-[#fafbfc] rounded-lg p-3 text-center">
                    <p className="text-[10px] text-[#f59e0b] font-bold uppercase tracking-wider">{frame.header}</p>
                    <p className="text-xs text-[#596065] mt-1">{frame.detail}</p>
                  </div>
                ))}
              </div>
            )}

            <div className="flex items-center justify-between mt-3">
              <p className="text-xs text-[#acb3b8]">
                Generated {videoInfo.generated_at ? new Date(videoInfo.generated_at).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'recently'}
              </p>
              {currentSchedule !== 'off' && (
                <p className="text-xs text-[#a78bfa] font-medium">
                  <Timer size={12} weight="bold" className="inline mr-1" />
                  Auto: {scheduleLabels[currentSchedule]}
                </p>
              )}
            </div>
          </div>
        ) : (
          <div className="text-center py-10" data-testid="video-empty-state">
            <FilmStrip size={48} className="mx-auto mb-3 text-[#d1d5db]" weight="duotone" />
            <p className="font-semibold text-[#2d3338] mb-1">No video summary yet</p>
            <p className="text-sm text-[#596065] mb-4">Generate a cinematic AI video recap with voice narration.</p>
            {!generating && (
              <button
                onClick={startGeneration}
                className="inline-flex items-center gap-2 bg-[#7c3aed] text-white font-semibold text-sm px-5 py-2.5 rounded-xl hover:bg-[#6d28d9] transition-colors"
                data-testid="generate-video-empty-btn"
              >
                <FilmStrip size={16} weight="fill" />
                Generate Video Summary
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
